import { TWILIO_CONFIG } from '../configs';
import { logger } from './logger';
import { UserAgentUtils } from './userAgentUtils';

interface IStopMediaParams {
  kind?: 'video' | 'audio';
  id?: string;
  tracks: MediaStreamTrack[];
}

const allTracksHashTable: { [key: string]: MediaStreamTrack } = {};

function getUpdatedConstraints(constraints?: MediaStreamConstraints) {
  const updatedConstraints = constraints || { audio: true, video: true };

  if (
    typeof updatedConstraints.video === 'object' &&
    updatedConstraints.video !== null &&
    Object.keys(updatedConstraints.video).length
  ) {
    return updatedConstraints;
  }

  if (updatedConstraints.video === false || updatedConstraints.video === undefined) {
    return updatedConstraints;
  }

  const isMobile =
    UserAgentUtils.isWebView() || UserAgentUtils.isMobileBrowser();
  updatedConstraints.video = isMobile
    ? TWILIO_CONFIG.videoCall.mobile.constraints
    : TWILIO_CONFIG.videoCall.desktop.constraints;
  return updatedConstraints;
}

async function startMedia(
  constraints?: MediaStreamConstraints
): Promise<MediaStreamTrack[] | null> {
  const attachTracks = (tracks: MediaStreamTrack[]) => {
    tracks.forEach((track) => {
      const data = {
        kind: track.kind,
        id: track.id,
        label: track.label,
        muted: track.muted,
        enabled: track.enabled,
      };
      allTracksHashTable[track.id] = track;
      logger({
        message: '[userMedia]: Started Track: ',
        data: data,
      });
    });
    return tracks;
  };

  const tryGetTracks = async (mediaConstraints: MediaStreamConstraints) => {
    const stream = await navigator.mediaDevices.getUserMedia(mediaConstraints);
    return attachTracks(stream.getTracks());
  };

  try {
    const updatedConstraints = getUpdatedConstraints(constraints);
    return await tryGetTracks(updatedConstraints);
  } catch (error) {
    try {
      const fallbackConstraints: MediaStreamConstraints = { audio: true, video: true };
      return await tryGetTracks(fallbackConstraints);
    } catch (fallbackError) {
      return null;
    }
  }
}

function stopMedia({ kind, id, tracks }: IStopMediaParams) {
  tracks.forEach((track) => {
    const data = {
      kind: track.kind,
      id: track.id,
      label: track.label,
      muted: track.muted,
      enabled: track.enabled,
    };

    if (kind || id) {
      if ((id && track.id === id) || (!id && track.kind === kind)) {
        track.stop();
        delete allTracksHashTable[track.id];
        logger({
          message: '[userMedia]: Stopped Track: ',
          data,
        });
      }
    } else {
      track.stop();
      delete allTracksHashTable[track.id];
      logger({
        message: '[UserMedia]: Stopped Track: ',
        data,
      });
    }
  });
}

function stopAllMedia() {
  const allTracks = Object.values(allTracksHashTable);
  if (!allTracks.length) {
    return;
  }
  logger({ message: '[userMedia]: Stopping All tracks', data: allTracks });

  allTracks.forEach((track) => {
    const data = {
      kind: track.kind,
      id: track.id,
      label: track.label,
      muted: track.muted,
      enabled: track.enabled,
    };
    track.stop();
    delete allTracksHashTable[track.id];
    logger({
      message: '[userMedia]: Stopped Track: ',
      data,
    });
  });
}

export const UserMediaUtils = {
  startMedia,
  stopMedia,
  stopAllMedia,
};
